/**
 * Le SEUL appel réseau du front, avec ses deux garanties : un délai, et un échec
 * qui a une forme.
 *
 * Il y avait quatre façons d'appeler l'API — `api.ts` levait une `AccessError`,
 * `useEspace` posait un état `failed`, `PasteStep` lisait un message dans le corps,
 * `CompanyPicker` avalait tout — et AUCUNE ne posait de délai. Une requête que le
 * serveur n'honore jamais laissait un écran en chargement pour toujours : pas
 * d'erreur, pas de bouton, rien à faire que recharger la page.
 *
 * Ce module ne masque pas les codes HTTP : il les rend. Un 401 n'est pas une panne
 * (c'est une session finie), un 429 n'est pas une panne (c'est trop vite), et les
 * appelants ont besoin de les distinguer pour dire la bonne chose.
 */

/** Au-delà, ce n'est plus une lenteur, c'est une panne. */
export const HTTP_TIMEOUT_MS = 15_000;
/**
 * La lecture d'annonce appelle un modèle : elle a le droit d'être lente.
 *
 * ⚠️ 90 s et pas 60, parce que le serveur peut légitimement mettre plus longtemps :
 * `readFromUrl` coupe à 12 s, PUIS l'appel au modèle coupe à 45 s, en série, et la
 * lecture d'un PDF de 3 Mo s'ajoute sans plafond. À 60 s, le navigateur abandonnait
 * une requête que le serveur allait honorer, et l'entreprise recommençait une
 * lecture payante qui venait d'aboutir.
 */
export const HTTP_TIMEOUT_LONG_MS = 90_000;

export type HttpFailure =
	/**
	 * Le serveur a répondu, mais pas ce qu'on voulait. `status` et `code` sont lisibles.
	 *
	 * `body` porte le corps parsé EN ENTIER, et pas seulement les deux champs qu'on en
	 * extrait. Sans lui, le dépôt d'une offre ne pouvait pas passer par ici : son 422
	 * rend `{ errors: [{ field, message }] }`, champ par champ, et jeter ce tableau
	 * effacerait « Le SIREN compte 9 chiffres » sous le champ concerné. Le formulaire
	 * serait alors resté le seul appel sans délai de l'application, c'est-à-dire celui
	 * qui laisse le bouton tourner pour toujours au moment le plus coûteux.
	 */
	| { kind: "http"; status: number; code: string; message: string | null; body: unknown }
	/** Le délai est écoulé : la requête a été abandonnée. */
	| { kind: "timeout" }
	/** Rien n'est parti, ou rien n'est revenu : hors ligne, DNS, TLS. */
	| { kind: "network" };

export class HttpError extends Error {
	constructor(readonly failure: HttpFailure) {
		super(failure.kind === "http" ? `${failure.status} ${failure.code}` : failure.kind);
		this.name = "HttpError";
	}

	get status(): number | null {
		return this.failure.kind === "http" ? this.failure.status : null;
	}

	/** Le code métier du serveur (`LINK_REVOKED`, `RATE_LIMITED`…), ou `null`. */
	get code(): string | null {
		return this.failure.kind === "http" ? this.failure.code : null;
	}
}

export type HttpOptions = {
	method?: "GET" | "POST" | "PATCH" | "DELETE";
	/** Sérialisé en JSON. Un `FormData` passe tel quel, sans en-tête forcé. */
	body?: unknown;
	timeoutMs?: number;
	signal?: AbortSignal;
};

/**
 * Appelle l'API et rend le corps JSON, ou lève une `HttpError`.
 *
 * ⚠️ `credentials: "same-origin"` est posé ici une fois pour toutes. Il était écrit
 * à la main dans onze appels, et il ne manquait qu'à un seul pour qu'une session
 * cesse d'être envoyée sans que rien ne le signale.
 */
export async function fetchJson<T>(path: string, options: HttpOptions = {}): Promise<T> {
	const { method = "GET", body, timeoutMs = HTTP_TIMEOUT_MS, signal } = options;

	const controller = new AbortController();
	const timer = setTimeout(
		() => controller.abort(new DOMException("timeout", "TimeoutError")),
		timeoutMs,
	);
	// L'appelant peut annuler lui-même (une frappe qui remplace la précédente dans
	// `CompanyPicker`) : les deux signaux valent abandon, et il faut les deux.
	const relais = () => controller.abort(signal?.reason);
	signal?.addEventListener("abort", relais);

	const estFormData = typeof FormData !== "undefined" && body instanceof FormData;

	try {
		const response = await fetch(path, {
			method,
			credentials: "same-origin",
			headers:
				body !== undefined && !estFormData ? { "Content-Type": "application/json" } : undefined,
			body:
				body === undefined ? undefined : estFormData ? (body as FormData) : JSON.stringify(body),
			signal: controller.signal,
		});

		if (!response.ok) {
			// Le serveur rend TOUJOURS du JSON sur /api (`app.onError`, `app.notFound`),
			// mais un proxy en amont peut rendre du HTML : on ne fait pas confiance.
			const payload = (await response.json().catch(() => ({}))) as {
				error?: string;
				message?: string;
			};
			throw new HttpError({
				kind: "http",
				status: response.status,
				code: payload.error ?? `HTTP_${response.status}`,
				message: payload.message ?? null,
				body: payload,
			});
		}

		// 204, ou un corps vide : `json()` lèverait, et l'appelant n'attend rien.
		if (response.status === 204) return undefined as T;
		return (await response.json()) as T;
	} catch (error) {
		if (error instanceof HttpError) throw error;
		/*
		 * ⚠️ L'abandon se reconnaît au SIGNAL, jamais au nom de l'erreur. On testait
		 * `error.name === "AbortError"` : `fetch` rejette avec la raison d'abandon
		 * telle qu'on l'a posée, et la nôtre s'appelle `TimeoutError`. Le test était
		 * donc toujours faux, et tout délai dépassé se rangeait en « panne réseau ».
		 * Une lecture d'annonce trop lente affichait « vérifiez votre accès à
		 * Internet » à quelqu'un dont la connexion allait très bien.
		 */
		const abandon = controller.signal.aborted;
		// Un abandon demandé par l'appelant reste un abandon : il le reconnaît à son
		// propre signal, et il ne doit pas afficher « la connexion a échoué ».
		if (abandon && signal?.aborted) throw error;
		throw new HttpError({ kind: abandon ? "timeout" : "network" });
	} finally {
		clearTimeout(timer);
		signal?.removeEventListener("abort", relais);
	}
}

/** Le message à montrer quand rien de plus précis n'existe. Jamais un code technique. */
export function messageDeSecours(error: unknown): string {
	if (error instanceof HttpError) {
		if (error.failure.kind === "timeout") {
			return "Le serveur met trop de temps à répondre. Réessayez dans un instant.";
		}
		if (error.failure.kind === "network") {
			return "La connexion a échoué. Vérifiez votre accès à Internet.";
		}
		if (error.failure.message) return error.failure.message;
		if (error.failure.status >= 500) {
			return "Le service est momentanément indisponible. Réessayez dans un instant.";
		}
	}
	return "Quelque chose n'a pas fonctionné. Réessayez dans un instant.";
}
